
/**
 * Modal para exportação do dashboard em PDF
 * 
 * O que faz:
 * - Exibe modal com preview do dashboard para exportação
 * - Permite escolher entre formato A4 e Mobile (1080x1920)
 * - Gera e faz download do PDF
 * - Mostra preview em tempo real do formato selecionado
 * 
 * Onde é usado:
 * - Integrado no Dashboard principal via botão no header
 * 
 * Como se conecta:
 * - Usa hook useDashboardExport para lógica de exportação
 * - Recebe dados do dashboard principal
 * - Utiliza DashboardReportDocument para renderizar o preview e o arquivo final
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useMemo, useRef } from 'react';
import { ExportFormat } from '@/hooks/useDashboardExport';
import { DashboardMetrics } from '@/hooks/dashboard/types';
import { Lead } from '@/hooks/useSupabaseLeads';
import { FileText, Smartphone, Download, Loader2 } from 'lucide-react';
import { DashboardReportDocument } from './report/DashboardReportDocument';
import { buildDashboardReportModel } from '@/features/dashboard/report/buildDashboardReportModel';
import { DASHBOARD_REPORT_FORMATS } from '@/features/dashboard/report/dashboardReportFormats';

interface DashboardExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFormat: ExportFormat;
  onFormatChange: (format: ExportFormat) => void;
  isExporting: boolean;
  onExport: (options: { format: ExportFormat; clinicaName?: string; periodo?: string; generatedAt: Date; sourceElement: HTMLElement }) => Promise<void>;
  dashboardData: DashboardMetrics | null;
  filteredRawLeads: Lead[];
  clinicaName?: string;
  periodo?: string;
  startDate: Date | null;
  endDate: Date | null;
  generatedAt: Date;
  getAdDisplayName: (adName: string | null) => string;
}

export const DashboardExportModal = ({
  isOpen,
  onClose,
  selectedFormat,
  onFormatChange,
  isExporting,
  onExport,
  dashboardData,
  filteredRawLeads,
  clinicaName,
  periodo,
  startDate,
  endDate,
  generatedAt,
  getAdDisplayName,
}: DashboardExportModalProps) => {
  const sourceRef = useRef<HTMLDivElement>(null);
  const reportModel = useMemo(() => dashboardData ? buildDashboardReportModel({
    metrics: dashboardData,
    leads: filteredRawLeads,
    clinicName: clinicaName || 'Clínica não identificada',
    periodLabel: periodo || 'Período selecionado',
    startDate,
    endDate,
    generatedAt,
    getAdDisplayName: (adName) => getAdDisplayName(adName),
    // Prioriza o resumo para que a exportação não dependa da lista bruta de contatos.
    resumo: dashboardData.resumo,
  }) : null, [dashboardData, filteredRawLeads, clinicaName, periodo, startDate, endDate, generatedAt, getAdDisplayName]);
  const previewConfig = DASHBOARD_REPORT_FORMATS[selectedFormat];
  const totalPreviewPages = selectedFormat === 'a4' ? 3 : 4;
  const previewScale = previewConfig.previewWidth / previewConfig.pixelWidth;

  // Função para lidar com a exportação
  const handleExport = () => {
    if (!sourceRef.current) return;
    void onExport({
      format: selectedFormat,
      clinicaName,
      periodo,
      generatedAt,
      sourceElement: sourceRef.current,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header do Modal */}
        <DialogHeader className="pb-4">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-6 w-6 text-blue-600" />
            Exportar Dashboard
          </DialogTitle>
        </DialogHeader>

        {/* Seletores de Formato */}
        <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">Formato:</span>
          
          <Button
            variant={selectedFormat === 'a4' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onFormatChange('a4')}
            className="flex items-center gap-2"
            disabled={isExporting}
          >
            <FileText className="h-4 w-4" />
            A4 (Impressão)
          </Button>
          
          <Button
            variant={selectedFormat === 'mobile' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onFormatChange('mobile')}
            className="flex items-center gap-2"
            disabled={isExporting}
          >
            <Smartphone className="h-4 w-4" />
            Mobile (1080x1920)
          </Button>

          <div className="ml-auto">
            <Button
              onClick={handleExport}
              disabled={isExporting || !reportModel}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Gerando PDF...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Baixar PDF
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Informações do Formato Selecionado */}
        <div className="flex items-center gap-4 mb-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
            <span>
              <strong>Formato {selectedFormat === 'a4' ? 'A4' : 'Mobile'}:</strong> {' '}
              {selectedFormat === 'a4' 
                ? '210×297mm (1754×2480px) - Ideal para impressão' 
                : '1080×1920px - Otimizado para visualização mobile'
              }
            </span>
          </div>
          
          {clinicaName && (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-600 rounded-full"></div>
              <span><strong>Clínica:</strong> {clinicaName}</span>
            </div>
          )}
          
          {periodo && (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
              <span><strong>Período:</strong> {periodo}</span>
            </div>
          )}
        </div>

        {/* Preview do Dashboard */}
        <div className="flex-1 overflow-auto border border-gray-300 rounded-lg bg-gray-100 p-4">
          {reportModel ? <div className="flex justify-center">
            <div style={{ width: `${previewConfig.previewWidth}px`, height: `${(previewConfig.pixelHeight * totalPreviewPages + 32 * (totalPreviewPages - 1)) * previewScale}px` }}>
              <div style={{ transform: `scale(${previewScale})`, transformOrigin: 'top left', width: `${previewConfig.pixelWidth}px` }}>
                <DashboardReportDocument format={selectedFormat} model={reportModel} />
              </div>
            </div>
          </div> : <div className="flex min-h-[360px] items-center justify-center text-sm text-muted-foreground">Carregando dados do relatório...</div>}
        </div>

        {reportModel && <div ref={sourceRef} data-dashboard-export-source aria-hidden="true" className="pointer-events-none fixed left-[-20000px] top-0">
          <DashboardReportDocument format={selectedFormat} model={reportModel} />
        </div>}

        {/* Rodapé com Informações */}
        <div className="pt-4 border-t text-center">
          <p className="text-sm text-gray-600">
            O preview usa o mesmo documento e as mesmas páginas do PDF. A redução na tela afeta somente a visualização.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
